import React from 'react';
import StageTrack from './StageTrack';

/**
 * TrackerPanel — the face of MY WORK (Epic 5 · S5.2, Part F).
 *
 * Every piece of work on one line against the same four stops, grouped by
 * park. The signature element (Law 6): twenty items read in two seconds and the
 * stops waiting on the client stand out.
 *
 * Groups: one per `facts.groups[]`.
 *   - One group  → no group header.
 *   - Several    → a `.gi-label` header carrying the group label + a total
 *                  (in the multi-park view the group label is the park name).
 *   - Each group shows ≤5 rows; a "Show all {total}" affordance under a group
 *     with more than 5 rows calls `onShowAll(group)` → a ListDetail drill.
 *
 * The stop column labels (the four stage names) render once above the FIRST row
 * of each group as `.gi-label` headers, aligned to the fixed stop positions.
 *
 * Empty tracker (Verde — 0 groups): renders an empty-state sentence + an Ask
 * button (Part F / acceptance "Verde: empty sentence + Ask button"). It does
 * NOT return null — the empty state is correct and carries the Ask CTA.
 */
const MAX_ROWS_PER_GROUP = 5;

const StopHeader = ({ steps }) => (
  <div data-testid="tracker-stop-header" className="hidden md:flex items-center gap-3 mb-1">
    <div className="flex-1" />
    <div className="w-56 shrink-0 grid grid-cols-[auto_1fr_auto_1fr_auto_1fr_auto] items-center">
      {steps.map((s, i) => (
        <React.Fragment key={s.key ?? i}>
          {i > 0 && <span />}
          <span className="gi-label text-gray-400 dark:text-gray-500 text-center truncate">
            {s.label}
          </span>
        </React.Fragment>
      ))}
    </div>
    <div className="w-20 shrink-0" />
  </div>
);

const GroupHeader = ({ group }) => (
  <div className="flex items-baseline justify-between gap-3 mb-1">
    <span className="gi-label text-gray-500 dark:text-gray-400">{group.label}</span>
    <span className="text-xs font-medium text-gray-400 dark:text-gray-500 tabular-nums">
      {group.items?.length ?? 0}
    </span>
  </div>
);

const TrackerPanel = ({ facts, multiPark = false, onOpenItem, onShowAll, onAsk }) => {
  const groups = Array.isArray(facts?.groups) ? facts.groups : [];

  // Empty tracker — empty sentence + Ask button (Verde). Not null.
  if (groups.length === 0) {
    return (
      <section data-testid="panel-tracker" className="rounded-xl bg-white dark:bg-gray-900 shadow-sm border border-gray-200 dark:border-gray-800 p-4">
        <p className="gi-body text-gray-600 dark:text-gray-300">
          {facts?.sentence || 'Nothing in flight right now.'}
        </p>
        <button
          type="button"
          onClick={onAsk}
          className="mt-3 inline-flex items-center justify-center min-h-[44px] px-4 rounded-lg text-sm font-medium text-white bg-[#32B1FF] hover:bg-[#1aa3f5] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]"
        >
          Ask for something
        </button>
      </section>
    );
  }

  return (
    <section data-testid="panel-tracker" className="rounded-xl bg-white dark:bg-gray-900 shadow-sm border border-gray-200 dark:border-gray-800 p-4">
      {groups.map((group, gi) => {
        const items = Array.isArray(group.items) ? group.items : [];
        const total = typeof group.total === 'number' ? group.total : items.length;
        const capped = items.slice(0, MAX_ROWS_PER_GROUP);
        const hasMore = items.length > MAX_ROWS_PER_GROUP;
        // Stop column labels once, above the first row of the group.
        const headerSteps = capped[0]?.steps || items[0]?.steps || [];
        return (
          <div key={group.label ?? gi} data-testid="tracker-group" data-group-label={group.label ?? ''} className={gi > 0 ? 'mt-5' : ''}>
            {multiPark && <GroupHeader group={{ ...group, items, total }} />}
            <StopHeader steps={headerSteps} />
            <ul>
              {capped.map((it) => (
                <StageTrack key={it.id ?? it.title} item={it} onOpenItem={onOpenItem} />
              ))}
            </ul>
            {hasMore && (
              <button
                type="button"
                onClick={() => onShowAll?.(group)}
                data-testid="tracker-show-all"
                className="mt-1 text-sm font-medium text-[#32B1FF] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF] rounded"
              >
                Show all {total}
              </button>
            )}
          </div>
        );
      })}
    </section>
  );
};

export default TrackerPanel;
